/**
 * Shared placement math for the "page tips" guide bubbles.
 *
 * Pure functions, no React, no DOM writes -- each *GuideSteps.jsx measures its
 * own target and bubble, then asks computePlacement() where the bubble goes.
 *
 * The rule the client set: the callout must never cover the thing it is
 * highlighting. So we try each side in turn and only accept a side where the
 * bubble both fits on screen AND does not overlap the target.
 */

const ARROW_SIZE = 20;

/** Do two rects touch, allowing for `gap` px of breathing room? */
export function rectsOverlap(a, b, gap = 8) {
  return !(
    a.right + gap <= b.left ||
    a.left >= b.right + gap ||
    a.bottom + gap <= b.top ||
    a.top >= b.bottom + gap
  );
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * @param {object}  o
 * @param {DOMRect} o.targetRect     Rect of the highlighted element.
 * @param {number}  o.bubbleWidth
 * @param {number}  o.bubbleHeight
 * @param {number}  o.viewportWidth
 * @param {number}  o.viewportHeight
 * @param {number} [o.bottomInset]   Space to keep clear at the bottom (mobile navbar).
 * @param {number} [o.margin]        Minimum distance from the viewport edges.
 * @param {number} [o.gap]           Distance between the bubble and the target.
 * @returns {{top:number,left:number,arrow:"top"|"bottom"|"left"|"right"|"none",arrowOffset:string}}
 *   `arrow` names the side of the BUBBLE the pointer sits on, matching the
 *   existing `.arrow-*` CSS classes. "none" means no side worked and the arrow
 *   is hidden rather than pointing at nothing.
 */
export function computePlacement({
  targetRect,
  bubbleWidth,
  bubbleHeight,
  viewportWidth,
  viewportHeight,
  bottomInset = 0,
  margin = 12,
  gap = 12,
}) {
  const minLeft = margin;
  const maxLeft = viewportWidth - bubbleWidth - margin;
  const minTop = margin;
  const maxTop = viewportHeight - bottomInset - bubbleHeight - margin;

  const centeredLeft = targetRect.left + targetRect.width / 2 - bubbleWidth / 2;
  const centeredTop = targetRect.top + targetRect.height / 2 - bubbleHeight / 2;

  // Ordered by preference. Below/above read most naturally; left/right are the
  // fallbacks that rescue edge-anchored panels like the trade ticket.
  const candidates = [
    { arrow: "top", top: targetRect.bottom + gap, left: clamp(centeredLeft, minLeft, maxLeft) },
    {
      arrow: "bottom",
      top: targetRect.top - gap - bubbleHeight,
      left: clamp(centeredLeft, minLeft, maxLeft),
    },
    { arrow: "left", left: targetRect.right + gap, top: clamp(centeredTop, minTop, maxTop) },
    {
      arrow: "right",
      left: targetRect.left - gap - bubbleWidth,
      top: clamp(centeredTop, minTop, maxTop),
    },
  ];

  const toRect = (c) => ({
    left: c.left,
    top: c.top,
    right: c.left + bubbleWidth,
    bottom: c.top + bubbleHeight,
  });

  const fits = (c) =>
    c.left >= minLeft &&
    c.left <= maxLeft &&
    c.top >= minTop &&
    c.top <= maxTop &&
    maxLeft >= minLeft &&
    maxTop >= minTop;

  const chosen =
    candidates.find((c) => fits(c) && !rectsOverlap(toRect(c), targetRect, gap / 2)) ||
    fallbackPlacement({
      targetRect,
      bubbleWidth,
      bubbleHeight,
      viewportWidth,
      viewportHeight,
      bottomInset,
      margin,
    });

  return { ...chosen, arrowOffset: arrowOffsetFor(chosen, targetRect, bubbleWidth, bubbleHeight) };
}

/**
 * Nothing fit cleanly (tiny viewport, or a target that fills the screen). Park
 * the bubble in whichever band has the most room, still without covering the
 * target, and drop the arrow -- a pointer aimed at the wrong place is worse
 * than no pointer.
 */
function fallbackPlacement({
  targetRect,
  bubbleWidth,
  bubbleHeight,
  viewportWidth,
  viewportHeight,
  bottomInset,
  margin,
}) {
  const space = {
    top: targetRect.top - margin,
    bottom: viewportHeight - bottomInset - targetRect.bottom - margin,
    left: targetRect.left - margin,
    right: viewportWidth - targetRect.right - margin,
  };
  const side = Object.keys(space).reduce((a, b) => (space[b] > space[a] ? b : a));

  const maxLeft = Math.max(margin, viewportWidth - bubbleWidth - margin);
  const maxTop = Math.max(margin, viewportHeight - bottomInset - bubbleHeight - margin);
  const centeredLeft = targetRect.left + targetRect.width / 2 - bubbleWidth / 2;
  const centeredTop = targetRect.top + targetRect.height / 2 - bubbleHeight / 2;

  const parked = {
    top: { top: margin, left: clamp(centeredLeft, margin, maxLeft) },
    bottom: { top: maxTop, left: clamp(centeredLeft, margin, maxLeft) },
    left: { left: margin, top: clamp(centeredTop, margin, maxTop) },
    right: { left: maxLeft, top: clamp(centeredTop, margin, maxTop) },
  }[side];

  return { ...parked, arrow: "none" };
}

/**
 * Where along the bubble's edge the pointer sits, as a percentage. Horizontal
 * for top/bottom arrows, vertical for left/right ones. Kept away from the
 * rounded corners so the triangle always has a flat edge to sit on.
 */
function arrowOffsetFor(placement, targetRect, bubbleWidth, bubbleHeight) {
  if (placement.arrow === "none") return "50%";

  if (placement.arrow === "top" || placement.arrow === "bottom") {
    const targetCenter = targetRect.left + targetRect.width / 2;
    const percent = ((targetCenter - placement.left) / bubbleWidth) * 100;
    const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
    return `${clamp(percent, limit, 100 - limit)}%`;
  }

  const targetCenter = targetRect.top + targetRect.height / 2;
  const percent = ((targetCenter - placement.top) / bubbleHeight) * 100;
  const limit = (ARROW_SIZE / bubbleHeight) * 100 + 4;
  return `${clamp(percent, limit, 100 - limit)}%`;
}
